import style from "./color-picker.module.css";
import { DropdownButton } from "../toolbar-buttons";
import { OutlineColor, SolidColor } from "frontend/ui-components/color-opacity-picker";
import { combineColorOpacity, parseColor, replaceColorOpacity } from "frontend/utils/color-utils";
import classNames from "classnames";
import { OpacitySlider } from "frontend/ui-components/design-system/opacity-slider/opacity-slider";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAccountReps } from "frontend/hooks/use-account-reps";
import CustomColorsPicker from "frontend/ui-components/custom-colors-picker";

// Some places use 'transparent' and pthers '#00000000'.
// This checks for both forms of transparent color.
function isTransparent(color: string): boolean {
  return color == "transparent" || color == "#00000000";
}

export function ColorPicker({
  value,
  onChange,
  multi,
  colorPalette,
  customColors,
  withTransparency = false,
  tooltip,
  onlyOutline = false,
  allowCustomColor = false,
  isSplitCell: _isSplitCell = false,
  theme = "dark",
  boundary = "no-controls-area",
  editable = true,
  portalId = "",
  margin = 8,
}: {
  // ---- requirement props for multiValuePicker function
  value?: string;
  onChange: (color: string, addUndo: boolean) => void;
  multi: boolean;
  // ---- everything under this line must be optional
  colorPalette?: any;
  customColors?: any;
  withTransparency?: boolean;
  tooltip?: string;
  onlyOutline?: boolean;
  allowCustomColor?: boolean;
  isSplitCell?: boolean;
  theme?: "dark" | "light";
  boundary?: string;
  editable?: boolean;
  portalId?: string;
  margin?: number;
}) {
  function renderColorPalette(close: () => void) {
    return (
      <div style={{ padding: "8px" }}>
        <ColorPickerPopup
          value={value ?? "white"}
          onChange={(c, addUndo) => {
            if (typeof c == "number") {
              c = replaceColorOpacity(value, c);
            }
            onChange?.(c, addUndo);
          }}
          colorPalette={colorPalette}
          customColors={customColors}
          allowCustomColor={allowCustomColor}
          withTransparency={withTransparency}
          closePopup={close}
        />
      </div>
    );
  }

  const iconColor = multi
    ? "conic-gradient(#ff4800, #dfd902, #20dc68, #0092f4, #da54d8, #ff4800)"
    : value || "transparent";

  return (
    <DropdownButton
      tooltip={tooltip ?? "Change color"}
      renderPopup={renderColorPalette}
      data-testid={onlyOutline ? "strokecolorpicker" : "FloatingColorPicker"}
      className={classNames(style.colorPopup, style[theme])}
      boundary={boundary}
      editable={editable}
      portalId={portalId}
      arrowSize={0}
      margin={margin}
    >
      {onlyOutline ? <OutlineColor color={iconColor} /> : <SolidColor color={iconColor} />}
    </DropdownButton>
  );
}

export function ColorPickerPopup({
  value,
  onChange,
  colorPalette,
  customColors,
  allowCustomColor,
  withTransparency,
  closePopup: _closePopup,
}: {
  value: string;
  onChange: (color: string, addUndo: boolean) => void;
  colorPalette: string[];
  customColors?: string[];
  allowCustomColor?: boolean;
  withTransparency?: boolean;
  closePopup: () => void;
}) {
  const { color: baseColor, opacity } = parseColor(value);

  const [showCustomColorPicker, setShowCustomColorPicker] = useState(false);
  const { isAccountRepsReady, addCustomColorForUser } = useAccountReps();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [customPickerPosition, setCustomPickerPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (showCustomColorPicker && buttonRef.current) {
      const { x, y, width, height } = buttonRef.current.getBoundingClientRect();
      setCustomPickerPosition({ x: x + width / 2, y: y + height / 2 });
    }
  }, [showCustomColorPicker]);

  const handleCustomColorChange = useCallback(
    (color: string) => {
      setShowCustomColorPicker(false);

      if (colorPalette.indexOf(color) != -1) return;

      if (isAccountRepsReady) {
        addCustomColorForUser(color);
      }
      onChange(combineColorOpacity(color, opacity || 255), true);
    },
    [opacity, isAccountRepsReady]
  );

  const smallPalette = colorPalette.length < 8;
  const layout = smallPalette
    ? {
        display: "flex",
        flexFlow: "row nowrap",
        gap: "2px",
      }
    : {
        display: "grid",
        gridTemplateColumns: "repeat(8,1fr)",
        gap: "2px",
      };

  return (
    <>
      {withTransparency && (
        <div className={style.opacitySliderWrapper}>
          <OpacitySlider
            value={Math.round((100 * opacity) / 255)}
            onChange={function (v: number): void {
              const c = replaceColorOpacity(baseColor, (255 * v) / 100);
              onChange(c, false);
            }}
            onFinish={function (v: number): void {
              const c = replaceColorOpacity(baseColor, (255 * v) / 100);
              onChange(c, true);
            }}
            color={baseColor}
          />
        </div>
      )}
      <div style={layout}>
        {colorPalette.map((color) => (
          <button
            key={color}
            className={style.button}
            onClick={() => {
              onChange(replaceColorOpacity(color, opacity), true);
            }}
          >
            <div
              style={{
                background: isTransparent(color) ? "url(/images/grey-checkerboard.png) repeat center/80px" : color,
                border: "1px solid #454545",
                borderRadius: "50%",
                width: "19px",
                height: "19px",
              }}
              data-testid={color}
            />
          </button>
        ))}
        {allowCustomColor &&
          customColors &&
          customColors.map((color, index) => (
            <button
              key={`${index}-${color}`}
              className={style.button}
              onClick={() => {
                onChange(replaceColorOpacity(color, opacity), true);
              }}
              draggable={true}
              onDragStart={(e) => {
                e.currentTarget.style.opacity = "0.5"; // perhaps outline ?
                e.dataTransfer.setData("text/plain", color);
                e.dataTransfer.setData("application/x-canvas-color", index.toString());
                e.dataTransfer.setDragImage(getColorIconImage(color), 11, 11);
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragEnd={(e) => {
                e.currentTarget.style.opacity = "1";
              }}
            >
              <div
                style={{
                  background: color,
                  border: "1px solid #454545",
                  borderRadius: "50%",
                  width: "19px",
                  height: "19px",
                }}
                data-testid={color}
              />
            </button>
          ))}
        {allowCustomColor && (
          <button className={style.button} ref={buttonRef} onClick={() => setShowCustomColorPicker((x) => !x)}>
            <svg xmlns="http://www.w3.org/2000/svg" width={19} height={19} fill="none" viewBox="0 0 19 19">
              <circle cx={9.4} cy={9.4} r={8.9} stroke="#666" />
              <path stroke="#666" strokeLinecap="round" d="M5.7 9.5h7.4M9.3 5.7v7.4" />
            </svg>
          </button>
        )}
      </div>
      {showCustomColorPicker && (
        <div
          style={{
            cursor: "auto",
            position: "fixed",
            zIndex: 100,
            top: customPickerPosition.y,
            left: customPickerPosition.x,
          }}
        >
          <CustomColorsPicker
            hex={baseColor}
            onChange={(color: string) => {
              onChange(combineColorOpacity(color, opacity), false);
            }}
            onDismiss={(color: string) => {
              handleCustomColorChange(color);
            }}
          />
        </div>
      )}
    </>
  );
}

function getColorIconImage(color: string) {
  let div = document.getElementById("color-drag-icon");
  if (!div) {
    div = document.createElement("div");
    div.id = "color-drag-icon";
    div.style.width = "22px";
    div.style.height = "22px";
    div.style.borderRadius = "50%";
    div.style.position = "absolute";
    div.style.top = "-9999px";
    document.body.appendChild(div);
  }
  div.style.backgroundColor = color;
  return div;
}
